'use client';

import React, { useState } from 'react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { WhatsAppInlineButton } from '@/components/WhatsAppButton';
import SuccessModal from '@/app/components/SuccessModal';
import { trackOfferteFormSubmit } from '@/lib/conversions';
import MultiSelectDienst from '@/app/components/MultiSelectDienst';
import PageHero from '@/components/responsive/PageHero';

interface FormData {
  naam: string;
  telefoon: string;
  email: string;
  diensten: string[];
  merk: string;
  model: string;
  bouwjaar: string;
  chassisnummer: string;
  bericht: string;
}

export default function OffertePage() {
  const [formData, setFormData] = useState<FormData>({
    naam: '', telefoon: '', email: '', diensten: [],
    merk: '', model: '', bouwjaar: '', chassisnummer: '', bericht: '',
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sendError, setSendError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [telefoonError, setTelefoonError] = useState('');

  const update = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'email') setEmailError('');
    if (field === 'telefoon') setTelefoonError('');
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      setEmailError('E-mailadres is verplicht.');
      return;
    }
    if (!validateEmail(formData.email)) {
      setEmailError('Voer een geldig e-mailadres in.');
      return;
    }
    setSending(true);
    setSendError('');
    setEmailError('');
    setTelefoonError('');
    try {
      const res = await fetch('/api/send-offerte', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          dienst: formData.diensten.join(', '),
          diensten: formData.diensten,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.validationErrors) {
          if (data.validationErrors.email) setEmailError(data.validationErrors.email);
          if (data.validationErrors.telefoon) setTelefoonError(data.validationErrors.telefoon);
        } else {
          setSendError('Er is een fout opgetreden. Probeer opnieuw of bel ons.');
        }
      } else {
        trackOfferteFormSubmit(
          formData.diensten[0] || '',
          formData.merk ? `${formData.merk} ${formData.model}`.trim() : formData.diensten[0] || '',
          { location: 'offerte-page' }
        );
        setSubmitted(true);
      }
    } catch {
      setSendError('Er is een fout opgetreden. Probeer opnieuw of bel ons.');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setEmailError('');
    setTelefoonError('');
    setFormData({ naam: '', telefoon: '', email: '', diensten: [], merk: '', model: '', bouwjaar: '', chassisnummer: '', bericht: '' });
  };

  return (
    <>
      <SuccessModal visible={submitted} naam={formData.naam} onClose={resetForm} />
      <Header />
      <main>
        <PageHero
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Offerte' },
          ]}
          title="Offerte Roetfilter Center"
          description="Roetfilter probleem? Wat zijn de kosten? Vraag hier uw vrijblijvende offerte aan. Wij contacteren u zo snel mogelijk met een transparante prijsopgave."
          whatsappMessage="Hallo, ik wil graag een offerte aanvragen voor mijn roetfilter."
        />

        <section className="rc-section bg-white">
          <div className="rc-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div>
                <h2 className="text-3xl font-extrabold text-foreground mb-6">Vraag uw Vrijblijvende Offerte Aan</h2>
                <p className="text-muted-foreground mb-6">Heeft u een algemene vraag of opmerking? Neem gerust contact met ons op. Wilt u graag een offerte op maat? Vul dan eenvoudig het onderstaande formulier in.</p>
                <div className="rc-card-soft p-4 sm:p-6">
                  {submitted ? (
                    <div className="text-center py-10 space-y-4">
                      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                        <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h4 className="text-xl font-extrabold text-foreground">Offerte aanvraag verzonden!</h4>
                      <p className="text-sm text-muted-foreground">Bedankt, {formData.naam}. Wij nemen zo snel mogelijk contact met u op — meestal binnen 24u.</p>
                      <button onClick={resetForm} className="text-sm text-primary hover:underline">
                        Nieuwe aanvraag indienen
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-foreground mb-2">Naam *</label>
                          <input
                            type="text"
                            required
                            value={formData.naam}
                            onChange={(e) => update('naam', e.target.value)}
                            className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                            placeholder="Uw naam"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-foreground mb-2">Telefoon *</label>
                          <input
                            type="tel"
                            required
                            value={formData.telefoon}
                            onChange={(e) => update('telefoon', e.target.value)}
                            className={`w-full border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white ${telefoonError ? 'border-red-500' : 'border-border'}`}
                            placeholder="Uw telefoonnummer"
                            inputMode="tel"
                          />
                          {telefoonError && <p className="text-sm text-red-600 mt-1">{telefoonError}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-foreground mb-2">E-mail *</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => update('email', e.target.value)}
                          className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                          placeholder="Uw e-mailadres"
                          inputMode="email"
                          autoComplete="email"
                        />
                        {emailError && <p className="text-sm text-red-600 mt-1">{emailError}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-foreground mb-2">Probleem / Dienst</label>
                        <MultiSelectDienst
                          value={formData.diensten}
                          onChange={(selected) => setFormData((prev) => ({ ...prev, diensten: selected }))}
                          inputClassName="py-4 text-base"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-foreground mb-2">Merk</label>
                          <input
                            type="text"
                            value={formData.merk}
                            onChange={(e) => update('merk', e.target.value)}
                            className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                            placeholder="bv. BMW"
                            autoComplete="off"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-foreground mb-2">Model</label>
                          <input
                            type="text"
                            value={formData.model}
                            onChange={(e) => update('model', e.target.value)}
                            className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                            placeholder="bv. 320d"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-foreground mb-2">Bouwjaar</label>
                          <input
                            type="text"
                            value={formData.bouwjaar}
                            onChange={(e) => update('bouwjaar', e.target.value)}
                            className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                            placeholder="bv. 2018"
                            inputMode="numeric"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-foreground mb-2">Chassisnummer (optioneel)</label>
                          <input
                            type="text"
                            value={formData.chassisnummer}
                            onChange={(e) => update('chassisnummer', e.target.value)}
                            className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                            placeholder="VIN nummer"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-foreground mb-2">Bericht / Beschrijving probleem</label>
                        <textarea
                          rows={3}
                          value={formData.bericht}
                          onChange={(e) => update('bericht', e.target.value)}
                          className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white resize-none"
                          placeholder="Beschrijf uw probleem of stel uw vraag"
                        />
                      </div>
                      {sendError && (
                        <p className="text-sm text-red-600">{sendError}</p>
                      )}
                      <button
                        type="submit"
                        disabled={sending}
                        className="w-full bg-primary text-white py-5 rounded-full font-extrabold text-base hover:opacity-90 transition-opacity disabled:opacity-60 min-h-[56px]"
                      >
                        {sending ? 'Verzenden...' : '✉️ Verstuur Offerte Aanvraag'}
                      </button>
                      <div className="flex items-center gap-3 my-1">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-xs text-muted-foreground font-medium">of</span>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                      <a
                        href="tel:0498545474"
                        className="w-full flex items-center justify-center gap-2 border-2 border-primary text-primary py-4 rounded-full font-bold text-base hover:bg-primary/5 transition-colors min-h-[56px]"
                      >
                        📞 Bel Direct: 0498 54 54 74
                      </a>
                      <WhatsAppInlineButton
                        label="Stuur een bericht"
                        message="Hallo, ik wil graag een offerte aanvragen."
                        className="w-full justify-center py-4 text-base min-h-[56px]"
                      />
                    </form>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <div className="rc-card p-6 lg:p-7 border-primary/20 bg-primary/5">
                  <h3 className="font-extrabold text-foreground mb-4">Indicatieve Prijzen (losse roetfilter)</h3>
                  {[
                    { type: "Reiniging auto's", price: '€195 – €250' },
                    { type: 'Reiniging Jeep / SUV', price: '€220 – €270' },
                    { type: 'Reiniging bestelwagens', price: '€250 – €350' },
                    { type: 'Reiniging trucks / bussen', price: '€490 – €750' },
                    { type: 'Revisie', price: 'Vanaf €695' },
                    { type: 'Diagnose', price: '€75 (terugbetaald bij herstelling)' },
                  ].map((r) => (
                    <div key={r.type} className="flex justify-between py-2 border-b border-border last:border-0">
                      <span className="text-sm font-medium text-foreground">{r.type}</span>
                      <span className="font-extrabold text-primary text-sm">{r.price}</span>
                    </div>
                  ))}
                </div>

                <div className="rc-card-soft p-6 lg:p-7">
                  <h3 className="font-extrabold text-foreground mb-3">🎁 Actie: Gratis Emissietest</h3>
                  <ul className="space-y-2">
                    {['Diagnose (€75) terugbetaald bij herstelling', 'Emissietest vooraf: GRATIS (was €30)', 'Eindmeting emissiewaarden: GRATIS (was €30)', 'Uw voordeel: €135'].map((s) => (
                      <li key={s} className="flex items-start gap-2 text-sm text-foreground"><span className="text-primary font-bold">✓</span> {s}</li>
                    ))}
                  </ul>
                </div>

                <div className="rc-card-soft p-6 lg:p-7">
                  <h3 className="font-extrabold text-foreground mb-3">Contact</h3>
                  <div className="space-y-2">
                    <p className="text-sm text-foreground"><strong>📞</strong> <a href="tel:0498545474" className="text-primary hover:underline">0498 54 54 74</a></p>
                    <p className="text-sm text-foreground"><strong>✉️</strong> <a href="mailto:info@roetfilter-center.be" className="text-primary hover:underline">info@roetfilter-center.be</a></p>
                    <p className="text-sm text-foreground"><strong>📍</strong> Dijkstraat 22 – 9160 Lokeren</p>
                    <div className="pt-2">
                      <WhatsAppInlineButton
                        label="Stuur een bericht"
                        message="Hallo, ik wil graag een offerte aanvragen."
                        className="px-4 py-2.5 text-sm w-full justify-center"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
